// The once-a-day sign-in desk prompt (CU-869fmdxu8), in the call pill's style: one line,
// "Which desk are you at?" with the suggested desk (✓ confirms it in one click), Change ▾ (search;
// handsets shared with me first, else by site), Not today and ×. It never blocks the page.
// In the dashboard header's slot (headerDeskSlot.js) it sits in the header row and folds to a
// "Pick desk" chip on a narrow header or while a call pill is up. Without that header it floats
// at the top centre, and on narrow screens (< 600 px) it is a small bottom sheet.
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Box, Button, ClickAwayListener, IconButton, InputAdornment, List, ListItemButton, ListItemText, ListSubheader,
    Paper, Popper, Stack, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../Iconify';
import { headerInset } from '../callPill/callPillPosition';
import { findHeaderDeskSlot } from './headerDeskSlot';
import { CONNECTION, deskSite, deskSummary, heldByName, heldBySomeoneElse, pickerSections } from './outboundModel';

const PROMPT = 'Which desk are you at?';
const PILL_MAX_WIDTH = 'min(640px, calc(100vw - 24px))';
// Just under MUI's dialog layer: page dialogs and the call pill / call dialogs (callLayers.js)
// all draw above this prompt, which can wait.
const PROMPT_LAYER = (theme) => theme.zIndex.modal - 1;
const RECHECK_MS = 1000;

// Pinned to the top of the screen, just under the app header, and centred.
//
// It used to follow the page's own header row (headerBottom, the call pill's rule), which put it
// a third of the way down a page with a tall header -- on Call Logs it landed on the tab row,
// reading as something that had come adrift rather than something anchored. The call pill keeps
// that rule because it is draggable and lives for the length of a call; this is a one-line prompt
// that appears once a day and is dismissed in a click, so it belongs in the same place every time.
//
// Still steps below the call pill when one is at the top: a live call keeps the spot. Re-checked
// like the call pill re-checks the header (pills come and go).
const promptTop = () => {
    const base = headerInset(window.innerWidth);
    const centre = window.innerWidth / 2;
    return Array.from(document.querySelectorAll('[data-testid="call-pill"], [data-testid="call-pill-dot"]'))
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.height > 0 && r.top < base + 56 && r.left < centre + 320 && r.right > centre - 320)
        .reduce((top, r) => Math.max(top, Math.round(r.bottom) + 8), base);
};

function usePromptTop(enabled) {
    const [top, setTop] = useState(promptTop);
    useEffect(() => {
        if (!enabled) return undefined;
        const update = () => setTop(promptTop());
        update();
        window.addEventListener('resize', update);
        const timer = setInterval(update, RECHECK_MS);
        return () => {
            window.removeEventListener('resize', update);
            clearInterval(timer);
        };
    }, [enabled]);
    return top;
}
// In the dashboard header (CU-869fn0jee follow-up): the prompt used to float just under the top
// bar and covered the page title. With the header's slot present it renders in the row itself,
// 40 px like the practice selector, and folds to a "Pick desk" chip on a narrow header or while
// a call pill is up (the call pill sits at the top centre; the chip stays clear of it).
const callPillShowing = () => Boolean(document.querySelector('[data-testid="call-pill"], [data-testid="call-pill-dot"]'));
const COMPACT_HEADER = '(max-width:1023.95px)';

function useHeaderPlacement() {
    const [state, setState] = useState(() => ({ slot: findHeaderDeskSlot(), callUp: callPillShowing() }));
    useEffect(() => {
        const update = () => setState((prev) => {
            const next = { slot: findHeaderDeskSlot(), callUp: callPillShowing() };
            return next.slot === prev.slot && next.callUp === prev.callUp ? prev : next;
        });
        update();
        const timer = setInterval(update, RECHECK_MS);
        return () => clearInterval(timer);
    }, []);
    return state;
}

const headerBarSx = {
    height: 40,
    boxSizing: 'border-box',
    minWidth: 0,
    maxWidth: '100%',
    borderRadius: 999,
    bgcolor: 'grey.900',
    color: 'common.white',
};

const onDark = {
    color: 'common.white',
    // App.css capitalises every button (!important); the pill reads as a sentence.
    textTransform: 'none !important',
    borderRadius: 999,
    flexShrink: 0,
    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
};

function DeskList({ desks, homeSite, heldDeskId, usualDeskId, showCallSetup, busy, onPick, maxHeight }) {
    const [query, setQuery] = useState('');
    const [showOther, setShowOther] = useState(false);
    const groups = useMemo(() => pickerSections(desks, { query, homeSite }), [desks, query, homeSite]);
    return (
        <>
            <Box sx={{ p: 1, pb: 0.5 }}>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search desks or extensions"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Iconify icon="mdi:magnify" width={18} /></InputAdornment>,
                        inputProps: { 'aria-label': 'Search desks', 'data-testid': 'desk-prompt-search' },
                    }}
                />
            </Box>
            <List dense disablePadding sx={{ maxHeight, overflowY: 'auto', pb: 0.5 }} data-testid="desk-prompt-list">
                {groups.length === 0 && (
                    <Typography variant="body2" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>No desks match “{query.trim()}”.</Typography>
                )}
                {groups.map(({ key, label, desks: list, collapsible }) => (
                    <li key={key}>
                        <ul style={{ padding: 0 }}>
                            {collapsible ? (
                                <ListSubheader
                                    component="button"
                                    type="button"
                                    onClick={() => setShowOther((v) => !v)}
                                    aria-expanded={showOther}
                                    sx={{ lineHeight: '28px', fontSize: 12, fontWeight: 700, width: 1, textAlign: 'left', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                    data-testid={`desk-prompt-section-${key}`}
                                >
                                    {label} ({list.length})
                                    <Iconify icon={showOther ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} sx={{ ml: 0.5 }} />
                                </ListSubheader>
                            ) : (
                                <ListSubheader
                                    sx={{ lineHeight: '28px', fontSize: 12, fontWeight: 700 }}
                                    data-testid={key.startsWith('site-') ? `desk-prompt-${key}` : `desk-prompt-section-${key}`}
                                >
                                    {label}
                                </ListSubheader>
                            )}
                            {(!collapsible || showOther) && list.map((d) => {
                                const heldBy = heldBySomeoneElse(d);
                                const notes = [
                                    String(d.id) === String(heldDeskId) && 'You\'re here',
                                    String(d.id) === String(usualDeskId) && 'Your usual desk',
                                    heldBy && `in use by ${heldByName(heldBy)}`,
                                    d.held_elsewhere && 'in use at another practice',
                                    showCallSetup && d.connection_state !== CONNECTION.CONNECTED && 'Calls from CRM: not set up',
                                ].filter(Boolean);
                                return (
                                    <ListItemButton key={d.id} disabled={busy} onClick={() => onPick(d)} data-testid={`desk-prompt-option-${d.id}`}>
                                        <ListItemText
                                            primary={deskSummary(d)}
                                            secondary={notes.join(' · ') || null}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                            secondaryTypographyProps={{ variant: 'caption', color: heldBy || d.held_elsewhere ? 'warning.dark' : 'text.secondary' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </ul>
                    </li>
                ))}
            </List>
        </>
    );
}

DeskList.propTypes = {
    desks: PropTypes.array.isRequired,
    homeSite: PropTypes.string,
    heldDeskId: PropTypes.string,
    usualDeskId: PropTypes.string,
    showCallSetup: PropTypes.bool,
    busy: PropTypes.bool,
    onPick: PropTypes.func.isRequired,
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function DeskPromptPill({
    desks, suggested, heldDeskId, usualDeskId, showCallSetup, busy, startOpen, onPick, onDismiss,
}) {
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down('sm'));
    const compactHeader = useMediaQuery(COMPACT_HEADER);
    const { slot, callUp } = useHeaderPlacement();
    const [changeOpen, setChangeOpen] = useState(Boolean(startOpen));
    // A state ref, so the list can open on the first render (startOpen) once the bar exists.
    const [bar, setBar] = useState(null);
    const changeRef = useRef(null);
    const top = usePromptTop(!narrow && !slot);
    // The practice's own site first: the site of the suggested desk (desks carry no site field).
    const homeSite = deskSite(suggested);
    const listProps = { desks, homeSite, heldDeskId, usualDeskId, showCallSetup, busy, onPick };
    const toggle = () => setChangeOpen((v) => !v);
    // Escape closes the list and gives focus back to Change (the search box it was in is gone).
    const onKeyDown = (e) => {
        if (e.key !== 'Escape' || !changeOpen) return;
        e.stopPropagation();
        setChangeOpen(false);
        changeRef.current?.focus();
    };

    const controls = (
        <>
            {suggested && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ pl: 1.25, pr: 0.25, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)', minWidth: 0, flexShrink: 1 }}
                    data-testid="desk-prompt-suggested"
                >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>{deskSummary(suggested)}</Typography>
                    <Tooltip title={`Yes, I'm at ${deskSummary(suggested)}`}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => onPick(suggested)}
                                aria-label={`Confirm ${deskSummary(suggested)}`}
                                data-testid="desk-prompt-confirm"
                                sx={{ ml: 0.25, color: 'grey.900', bgcolor: 'common.white', width: 26, height: 26, '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' } }}
                            >
                                <Iconify icon="mdi:check" width={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            )}
            <Button
                size="small"
                ref={changeRef}
                onClick={toggle}
                aria-expanded={changeOpen}
                aria-haspopup="dialog"
                endIcon={<Iconify icon={changeOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} />}
                data-testid="desk-prompt-change"
                sx={{ ...onDark, px: 1 }}
            >
                {suggested ? 'Change' : 'Pick desk'}
            </Button>
            <Button size="small" onClick={onDismiss} data-testid="desk-prompt-not-today" sx={{ ...onDark, px: 1, opacity: 0.8 }}>
                Not today
            </Button>
            <IconButton size="small" onClick={onDismiss} aria-label="Close" data-testid="desk-prompt-close" sx={{ ...onDark, ml: 'auto' }}>
                <Iconify icon="mdi:close" width={18} />
            </IconButton>
        </>
    );

    const listPopper = (
        <Popper
            open={changeOpen && Boolean(bar)}
            anchorEl={bar}
            placement="bottom"
            popperOptions={{ strategy: 'fixed' }}
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
            sx={{ zIndex: PROMPT_LAYER, width: 'min(380px, calc(100vw - 24px))' }}
        >
            <Paper elevation={12} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <DeskList {...listProps} maxHeight={360} />
            </Paper>
        </Popper>
    );

    if (slot) {
        const chip = compactHeader || callUp;
        const row = chip ? (
            <Stack
                ref={setBar}
                role="dialog"
                aria-label={PROMPT}
                direction="row"
                alignItems="center"
                data-testid="desk-prompt-pill"
                data-variant="chip"
                sx={{ ...headerBarSx, pl: 0.5, pr: 0.25 }}
            >
                <Button
                    size="small"
                    ref={changeRef}
                    onClick={toggle}
                    aria-expanded={changeOpen}
                    aria-haspopup="dialog"
                    startIcon={<Iconify icon="mdi:deskphone" width={18} />}
                    data-testid="desk-prompt-change"
                    sx={{ ...onDark, px: 1, whiteSpace: 'nowrap' }}
                >
                    Pick desk
                </Button>
                <Tooltip title="Not today">
                    <IconButton size="small" onClick={onDismiss} aria-label="Not today" data-testid="desk-prompt-close" sx={onDark}>
                        <Iconify icon="mdi:close" width={16} />
                    </IconButton>
                </Tooltip>
            </Stack>
        ) : (
            <Stack
                ref={setBar}
                role="dialog"
                aria-label={PROMPT}
                direction="row"
                alignItems="center"
                spacing={1}
                data-testid="desk-prompt-pill"
                data-variant="header"
                sx={{ ...headerBarSx, pl: 1.5, pr: 0.5 }}
            >
                <Iconify icon="mdi:deskphone" width={20} sx={{ flexShrink: 0, opacity: 0.85 }} />
                <Typography variant="subtitle2" noWrap sx={{ flexShrink: 0 }}>{PROMPT}</Typography>
                {controls}
            </Stack>
        );
        return createPortal(
            <ClickAwayListener onClickAway={() => setChangeOpen(false)} mouseEvent="onMouseDown">
                <Box onKeyDown={onKeyDown} sx={{ display: 'flex', minWidth: 0 }}>
                    {row}
                    {listPopper}
                </Box>
            </ClickAwayListener>,
            slot,
        );
    }

    if (narrow) {
        // Bottom sheet, not a modal: no backdrop, the page above stays usable.
        return (
            <Paper
                role="dialog"
                aria-label={PROMPT}
                elevation={12}
                onKeyDown={onKeyDown}
                data-testid="desk-prompt-pill"
                data-variant="sheet"
                sx={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: PROMPT_LAYER,
                    borderRadius: '16px 16px 0 0', overflow: 'hidden', bgcolor: 'grey.900', color: 'common.white',
                }}
            >
                <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{PROMPT}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>{controls}</Stack>
                </Box>
                {changeOpen && (
                    <Box sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
                        <DeskList {...listProps} maxHeight="45vh" />
                    </Box>
                )}
            </Paper>
        );
    }

    return (
        <ClickAwayListener onClickAway={() => setChangeOpen(false)} mouseEvent="onMouseDown">
            <Box onKeyDown={onKeyDown}>
                <Stack
                    ref={setBar}
                    role="dialog"
                    aria-label={PROMPT}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    data-testid="desk-prompt-pill"
                    data-variant="pill"
                    sx={{
                        position: 'fixed',
                        top,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: PROMPT_LAYER,
                        maxWidth: PILL_MAX_WIDTH,
                        minHeight: 48,
                        boxSizing: 'border-box',
                        pl: 1.5,
                        pr: 0.75,
                        py: 0.75,
                        borderRadius: 4,
                        bgcolor: 'grey.900',
                        color: 'common.white',
                        boxShadow: 8,
                    }}
                >
                    <Iconify icon="mdi:deskphone" width={20} sx={{ flexShrink: 0, opacity: 0.85 }} />
                    <Typography variant="subtitle2" noWrap sx={{ flexShrink: 0 }}>{PROMPT}</Typography>
                    {controls}
                </Stack>
                {listPopper}
            </Box>
        </ClickAwayListener>
    );
}

DeskPromptPill.propTypes = {
    desks: PropTypes.array.isRequired,
    // Usual desk, else the last desk on this browser; null = no suggestion (Pick desk opens the list).
    suggested: PropTypes.object,
    heldDeskId: PropTypes.string,
    usualDeskId: PropTypes.string,
    showCallSetup: PropTypes.bool,
    busy: PropTypes.bool,
    // Open with the desk list showing (back from "Pick another" in the clash dialog).
    startOpen: PropTypes.bool,
    onPick: PropTypes.func.isRequired,
    // × and Not today: hidden until tomorrow (the once-a-day rule already counted this showing).
    onDismiss: PropTypes.func.isRequired,
};
